import { action, extendObservable, reaction } from 'mobx';
import PropTypes from 'prop-types';
import FlagStore from '../FlagStore';
import InputStore from '../InputStore';
import FuseSelectStore from '../FuseSelectStore';
import RadioButtonStore from '../RadioButtonStore';
import { idpUrlSimpleSchemeValidator } from '../../urlPatternValidator';

export default class AuthenticationConfEditStore {
    static JIT_HELP_TXT = 'Just In Time provisioning allows previously un-provisioned users to be ' +
        'dynamically added at authentication time. Usually used when not using SCIM or LDAP bridge. ' +
        'To ensure successful user creation your SAML request must include the firstname and lastname attributes. ' +
        'You can also use the phone and groups (group names) attributes. If a group with the specified ' +
        'group name is not found, it is ignored.';
    static ASSUME_OWNERSHIP_HELP_TXT = 'Allows the IdP to control user synchronization as the master source. ' +
        'Usually used in conjunction with SCIM or LDAP bridge.';
    static AUTH_TYPE = { IDP: 'ownIDP', META: 'metaIDP' };
    static PROVISIONING_TYPE = { SCIM: 'SCIM', LDAP: 'LDAP', JIT: 'JIT' };
    static CONTEXT_CLASS_OPTIONS = [
        { value: 'PasswordProtectedTransport', label: 'PasswordProtectedTransport' },
        { value: 'Password', label: 'Password' },
        { value: 'X509', label: 'X509' },
        { value: 'Smartcard', label: 'Smartcard' },
        { value: 'Kerberos', label: 'Kerberos' },
        { value: 'null', label: 'Disabled' },
        { value: 'unspecified', label: 'Unspecified' },
    ];
    static INVALID_INPUT = 'Invalid entry';

    static PropType = PropTypes.shape({
        isConfEnabled: FlagStore.PropType.isRequired,
        justInTime: FlagStore.PropType.isRequired,
        idpAssumeOwnership: FlagStore.PropType.isRequired,
        isEditPasswordMode: FlagStore.PropType.isRequired,
        idProviderURL: InputStore.PropType.isRequired,
        idProviderIssuer: InputStore.PropType.isRequired,
        ssoURL: InputStore.PropType.isRequired,
        recipientURL: InputStore.PropType.isRequired,
        destinationURL: InputStore.PropType.isRequired,
        audienceURL: InputStore.PropType.isRequired,
        ldapURL: InputStore.PropType.isRequired,
        userName: InputStore.PropType.isRequired,
        password: InputStore.PropType.isRequired,
        userBaseDN: InputStore.PropType.isRequired,
        groupsBaseDN: InputStore.PropType.isRequired,
        usersFilter: InputStore.PropType.isRequired,
        groupsFilter: InputStore.PropType.isRequired,
        X_Certificate: InputStore.PropType.isRequired,
        LDAP_Certificate: InputStore.PropType.isRequired,
        contextClass: FuseSelectStore.PropType.isRequired,
        provisioningType: FuseSelectStore.PropType.isRequired,
        idpConfiguration: FuseSelectStore.PropType.isRequired,
        ldapType: FuseSelectStore.PropType.isRequired,
        isSCIM: PropTypes.bool.isRequired,
        isLDAP: PropTypes.bool.isRequired,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class AuthenticationConfEditStore */ {
            data: null,
            isAlreadyConfiguredIDP: false,
            isConfEnabled: new FlagStore(),
            idProviderURL: new InputStore(),
            idProviderIssuer: new InputStore(),
            ssoURL: new InputStore(),
            recipientURL: new InputStore(),
            destinationURL: new InputStore(),
            audienceURL: new InputStore(),
            ldapURL: new InputStore(),
            userName: new InputStore(),
            password: new InputStore(),
            dummyPassword: new InputStore({ value: '******' }),
            isEditPasswordMode: new FlagStore(),
            userBaseDN: new InputStore(),
            groupsBaseDN: new InputStore(),
            usersFilter: new InputStore(),
            groupsFilter: new InputStore(),
            X_Certificate: new InputStore(),
            LDAP_Certificate: new InputStore(),
            contextClass: new FuseSelectStore({ options: AuthenticationConfEditStore.CONTEXT_CLASS_OPTIONS }),
            provisioningType: new FuseSelectStore({ options: [
                { value: AuthenticationConfEditStore.PROVISIONING_TYPE.SCIM, label: 'SCIM' },
                { value: AuthenticationConfEditStore.PROVISIONING_TYPE.JIT, label: 'Just In Time (JIT) only' },
                { value: AuthenticationConfEditStore.PROVISIONING_TYPE.LDAP, label: 'LDAP AccessBridge' },
            ] }),
            idpConfiguration: new FuseSelectStore(),
            ldapType: new FuseSelectStore({ options: [
                { value: 'ActiveDirectory', label: 'Active Directory' },
                { value: 'OpenLDA', label: 'Open LDAP' },
            ] }),
            authType: new RadioButtonStore(AuthenticationConfEditStore.AUTH_TYPE.META),
            shortName: '',
            justInTime: new FlagStore(),
            idpAssumeOwnership: new FlagStore(),
            get isExternalMeta() {
                return this.authType.value === AuthenticationConfEditStore.AUTH_TYPE.META;
            },
            get isOwnIDP() {
                return this.authType.value === AuthenticationConfEditStore.AUTH_TYPE.IDP;
            },
            get isSCIM() {
                return this.provisioningType.selectedValues
                    .includes(AuthenticationConfEditStore.PROVISIONING_TYPE.SCIM);
            },
            get isLDAP() {
                return this.provisioningType.selectedValues
                    .includes(AuthenticationConfEditStore.PROVISIONING_TYPE.LDAP);
            },
            get isJIT() {
                return this.provisioningType.selectedValues
                    .includes(AuthenticationConfEditStore.PROVISIONING_TYPE.JIT);
            },
        }, data);

        this.idProviderURL.setValidator(idpUrlSimpleSchemeValidator);
        this.X_Certificate.setValidator((val) => {
            const regex = /^-{5}BEGIN CERTIFICATE-{5}(.|\n)+-{5}END CERTIFICATE-{5}$/;
            return regex.test(val);
        });

        reaction(() => this.dummyPassword.value, (newValue) => newValue && this.isEditPasswordMode.setFalse());

        reaction(() => this.provisioningType.selectedValues, (arr) => {
            if (this.isAlreadyConfiguredIDP || arr.length === 0) {
                return;
            }
            const isIdpCountEmpty = this.idpConfiguration.options.length === 0;
            if (arr[0] === AuthenticationConfEditStore.PROVISIONING_TYPE.JIT) {
                this.justInTime.setTrue();
                this.idpAssumeOwnership.setValue(isIdpCountEmpty);
            } else if (arr[0] === AuthenticationConfEditStore.PROVISIONING_TYPE.SCIM) {
                this.justInTime.setValue(isIdpCountEmpty);
                this.idpAssumeOwnership.setValue(isIdpCountEmpty);
            }
        });
    }

    setValue = action((data = {}) => {
        this.data = data;
        this.isAlreadyConfiguredIDP = data.isAlreadyConfiguredIDP;
        this.isConfEnabled.setValue(data.isConfEnabled);
        this.shortName = data.shortName;
        this.authType.setValue(data.authType);
        this.idpConfiguration.setOptions(data.idpList.map((item) => ({
            value: item.idp_response.id,
            label: item.idp_response.name,
        })));
        this.idpConfiguration.setSelectedValues(data.metaExternalIdpId ? [data.metaExternalIdpId] : []);

        this.ssoURL.setValue(data.linkSsoURL);
        this.recipientURL.setValue(data.linkRecipientUrl);
        this.destinationURL.setValue(data.linkDestinationUrl);
        this.audienceURL.setValue(data.linkAudienceUrl);

        this.provisioningType.setSelectedValues(data.provisioningType ? [data.provisioningType] : []);

        // SAML
        this.contextClass.setSelectedValues(data.contextClass ? [data.contextClass] : []);
        this.X_Certificate.setValue(data.xCertificate);
        this.idProviderIssuer.setValue(data.idProviderIssuer);
        this.idProviderURL.setValue(data.idProviderURL);

        // JIT
        this.justInTime.setValue(data.justInTime);
        this.idpAssumeOwnership.setValue(data.idpAssumeOwnership);

        // LDAP
        this.ldapType.setSelectedValues(data.ldapType ? [data.ldapType] : []);
        this.ldapURL.setValue(data.ldapURL);
        this.userName.setValue(data.userName);
        this.password.setValue(data.password);
        this.userBaseDN.setValue(data.userBaseDN);
        this.groupsBaseDN.setValue(data.groupsBaseDN);
        this.usersFilter.setValue(data.usersFilter);
        this.groupsFilter.setValue(data.groupsFilter);
        this.LDAP_Certificate.setValue(data.LDAP_Certificate);
    });

    Clear = action(() => {
        this.idpConfiguration.clear();
    });

    clearAfterDeleteIDP = action(() => {
        this.data = null;
        const hasIDPs = this.idpConfiguration.options.length > 0;

        this.isAlreadyConfiguredIDP = false;
        this.isConfEnabled.setValue(false);
        this.shortName = '';
        this.authType.setValue(AuthenticationConfEditStore.AUTH_TYPE.IDP);
        this.idpConfiguration.setSelectedValues([]);

        this.provisioningType.setSelectedValues([]);

        // SAML
        this.contextClass.setSelectedValues([]);
        this.X_Certificate.setValue('');
        this.idProviderIssuer.setValue('');
        this.idProviderURL.setValue('');
        this.justInTime.setValue(!hasIDPs);

        this.idpAssumeOwnership.setValue(!hasIDPs);

        // LDAP
        this.ldapType.setSelectedValues([]);
        this.ldapURL.setValue('');
        this.userName.setValue('');
        this.password.setValue('');
        this.userBaseDN.setValue('');
        this.groupsBaseDN.setValue('');
        this.usersFilter.setValue('');
        this.groupsFilter.setValue('');
        this.LDAP_Certificate.setValue('');
    });

    setErrorMessages = action(() => {
        if (!this.idProviderURL.isValid) {
            this.idProviderURL.errorMessage = AuthenticationConfEditStore.INVALID_INPUT;
        }

        if (!this.X_Certificate.isValid) {
            this.X_Certificate.errorMessage = AuthenticationConfEditStore.INVALID_INPUT;
        }
    });
    clearErrorMessages = action(() => {
        this.idProviderURL.errorMessage = '';
        this.X_Certificate.errorMessage = '';
    });

    isValid = () => {
        if (this.isExternalMeta && this.idpConfiguration.selectedValues.length === 0) {
            return false;
        }

        if (this.isOwnIDP && this.provisioningType.selectedValues.length === 0) {
            return false;
        }

        if (this.isOwnIDP
            && (this.idProviderIssuer.isEmpty
            || this.idProviderURL.isEmpty
            || this.X_Certificate.isEmpty
            || this.contextClass.selectedValues.length === 0)) {
            return false;
        }

        return !(this.isOwnIDP && this.isLDAP);
    };

    hasSwitchedToDisable = () => {
        return this.data && this.data.isConfEnabled && !this.isConfEnabled.value;
    };

    hasChanged = () => {
        if (this.data) {
            let retVal = this.isConfEnabled.value !== this.data.isConfEnabled
                || this.authType.value !== this.data.authType
                || this.idpConfiguration.getFirstValueOrEmpty() !== this.data.metaExternalIdpId;


            if (this.isOwnIDP) {
                retVal = retVal || this.idProviderURL.value !== this.data.idProviderURL
                    || this.idProviderIssuer.value !== this.data.idProviderIssuer
                    || this.X_Certificate.value !== this.data.xCertificate
                    || this.contextClass.getFirstValueOrEmpty() !== this.data.contextClass
                    || this.justInTime.value !== this.data.justInTime
                    || this.idpAssumeOwnership.value !== this.data.idpAssumeOwnership;
            }

            if (this.isLDAP) {
                retVal = retVal || this.ldapType.getFirstValueOrEmpty() !== this.data.ldapType
                    || this.ldapURL.value !== this.data.ldapURL
                    || this.userName.value !== this.data.userName
                    || (this.isEditPasswordMode.isTrue && this.password.value !== this.data.password)
                    || this.userBaseDN.value !== this.data.userBaseDN
                    || this.groupsBaseDN.value !== this.data.groupsBaseDN
                    || this.usersFilter.value !== this.data.usersFilter
                    || this.groupsFilter.value !== this.data.groupsFilter
                    || this.LDAP_Certificate.value !== this.data.LDAP_Certificate;
            }

            return retVal;
        } else if (this.data === null) {
            return true;
        }
        return false;
    };

    convertContextClassValueToDisplayName = (value) => {
        let retVal = '';
        if (value) {
            const found = AuthenticationConfEditStore.CONTEXT_CLASS_OPTIONS
                .find((el) => el.value.toLowerCase() === value.toLowerCase());
            if (found) {
                retVal = found.label;
            }
        }
        return retVal;
    };

    hasInvalidFields = () => {
        let retVal = false;
        if (this.isOwnIDP) {
            retVal = !this.idProviderURL.isValid || !this.X_Certificate.isValid;
        }
        return retVal;
    }
}
